import { createRecipeRequestSchema, rateRecipeRequestSchema, recipeDetailSchema, recipeListQuerySchema, recipeListResponseSchema, recipeNoteRequestSchema, recipeNotesResponseSchema, recipeRankingsQuerySchema, recipeRankingsResponseSchema, recipeTagsResponseSchema, reorderRecipeNotesRequestSchema, reportRequestSchema, reportResponseSchema, updateRecipeRequestSchema, } from '@fewerbags/schema';
const detail = { parse: recipeDetailSchema };
/** Query params for the two list endpoints; validated with the same schema the API uses. */
function listQuery(input = {}) {
    const q = recipeListQuerySchema.parse(input);
    return {
        q: q.q,
        tag: q.tag,
        cuisine: q.cuisine,
        course: q.course,
        visibility: q.visibility,
        sort: q.sort,
        limit: q.limit,
        offset: q.offset,
    };
}
function rankingsQuery(input = {}) {
    const q = recipeRankingsQuerySchema.parse(input);
    return {
        cuisine: q.cuisine,
        course: q.course,
        sortBy: q.sortBy,
        limit: q.limit,
        offset: q.offset,
    };
}
const notes = { parse: recipeNotesResponseSchema };
/** /api/v1/recipes — household-scoped; the household always comes from the session. */
export function createRecipesApi(http, base) {
    return {
        /** The caller's own household recipes. */
        list: async (input = {}) => http.get(base, { query: listQuery(input), parse: recipeListResponseSchema }),
        /** The shared layer: public recipes from every enabled household, never attributed (D12/D13). */
        listPublic: async (input = {}) => http.get(`${base}/public`, { query: listQuery(input), parse: recipeListResponseSchema }),
        /** Tags used by the household's own recipes, most used first. */
        tags: async () => http.get(`${base}/tags`, { parse: recipeTagsResponseSchema }),
        /** Tags used by public recipes from every enabled household (D13), most used first. */
        publicTags: async () => http.get(`${base}/public/tags`, { parse: recipeTagsResponseSchema }),
        get: async (id) => http.get(`${base}/${encodeURIComponent(id)}`, detail),
        create: async (input) => http.post(base, createRecipeRequestSchema.parse(input), detail),
        /** Partial update. `ingredients`/`steps` replace wholesale, and steps need ingredients with them. */
        update: async (id, input) => http.patch(`${base}/${encodeURIComponent(id)}`, updateRecipeRequestSchema.parse(input), detail),
        remove: async (id) => http.del(`${base}/${encodeURIComponent(id)}`),
        /** Copy a visible recipe into the caller's household; returns the new recipe. */
        fork: async (id) => http.post(`${base}/${encodeURIComponent(id)}/fork`, undefined, detail),
        /** Set the caller's own rating; returns the recipe with the refreshed aggregate. */
        rate: async (id, input) => http.put(`${base}/${encodeURIComponent(id)}/rating`, rateRecipeRequestSchema.parse(input), detail),
        /** Clear the caller's own rating. */
        unrate: async (id) => http.del(`${base}/${encodeURIComponent(id)}/rating`),
        /**
         * Rate for any person of the caller's household, with or without an account
         * (D30); records the caller as the rater. Returns the refreshed recipe.
         */
        ratePerson: async (id, personId, input) => http.put(`${base}/${encodeURIComponent(id)}/ratings/${encodeURIComponent(personId)}`, rateRecipeRequestSchema.parse(input), detail),
        /** Clear one person's rating; returns the refreshed recipe. */
        unratePerson: async (id, personId) => http.del(`${base}/${encodeURIComponent(id)}/ratings/${encodeURIComponent(personId)}`, undefined, detail),
        /**
         * Household recipes (plus public ones rated for someone in the household)
         * ranked by the household average or one person's stars (`sortBy` = a
         * household person id, D30).
         */
        rankings: async (input = {}) => http.get(`${base}/rankings`, {
            query: rankingsQuery(input),
            parse: recipeRankingsResponseSchema,
        }),
        /** Numbered notes are the caller's household's own (D26); every call returns the full list. */
        addNote: async (id, input) => http.post(`${base}/${encodeURIComponent(id)}/notes`, recipeNoteRequestSchema.parse(input), notes),
        updateNote: async (id, noteId, input) => http.patch(`${base}/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`, recipeNoteRequestSchema.parse(input), notes),
        removeNote: async (id, noteId) => http.del(`${base}/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`, undefined, notes),
        /** `noteIds` must list every note of the household on this recipe, in the new order. */
        reorderNotes: async (id, noteIds) => http.put(`${base}/${encodeURIComponent(id)}/notes/order`, reorderRecipeNotesRequestSchema.parse({ noteIds }), notes),
        /**
         * Report another household's public recipe (once per household; 409
         * ALREADY_REPORTED on repeat). Enough reports hide it pending review.
         */
        report: async (id, input) => http.post(`${base}/${encodeURIComponent(id)}/report`, reportRequestSchema.parse(input), {
            parse: reportResponseSchema,
        }),
    };
}
//# sourceMappingURL=recipes.js.map